import { lazy } from "react";

/**
 * Wrapper around React.lazy that recovers gracefully when a dynamic chunk
 * fails to load (typical after a deploy when the old chunk hash no longer
 * exists on the CDN).
 *
 * Behavior:
 *  - First attempt: normal dynamic import.
 *  - On failure: retry once after a short delay (handles transient network).
 *  - On second failure: dispatch a window event `app:update-required` so the
 *    AppErrorBoundary can show the "تحديث جديد متاح" banner, and resolve to
 *    a no-op component (returns null) so the React Suspense fallback does not
 *    stay stuck on a white loading screen forever.
 *
 * The user keeps full access to the *previous* page (router history is
 * intact) and chooses when to refresh via the banner button.
 */
function isChunkLoadError(err) {
  const msg = String(err?.message || "");
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk")
  );
}

function notifyUpdateRequired() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("app:update-required"));
  } catch {
    // ignore
  }
}

export function lazyWithRetry(factory) {
  return lazy(() =>
    factory().catch((err) => {
      if (!isChunkLoadError(err)) throw err;
      // One quick retry — covers transient network blips.
      return new Promise((resolve) => setTimeout(resolve, 600))
        .then(() => factory())
        .catch((err2) => {
          if (!isChunkLoadError(err2)) throw err2;
          // eslint-disable-next-line no-console
          console.warn(
            "[chunk-reload] dynamic import failed twice — surfacing update banner:",
            err2?.message
          );
          notifyUpdateRequired();
          // Resolve with a no-op component so Suspense unblocks immediately
          // (no white loading screen). The banner now sits over whatever the
          // router rendered and the user can refresh on their own terms.
          return { default: () => null };
        });
    })
  );
}

export default lazyWithRetry;